import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { makeStateKey, TransferState } from '@angular/core';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class FetchArrayDataService {
  private baseUrl = environment.baseUrl;
  private accountId = 1116; // You can make this dynamic if needed
  private isServer: boolean;

  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isServer = isPlatformServer(platformId);
  }

  // Helper method to create a unique state key
  private getStateKey(method: string, params?: any): string {
    return params ? `${method}-${JSON.stringify(params)}` : method;
  }

  // Generic method to fetch data with TransferState support
  private fetchWithTransferState<T>(method: string, url: string, params: any = {}): Observable<T> {
    const stateKey = makeStateKey<T>(this.getStateKey(method, params));

    if (!this.isServer && this.transferState.hasKey(stateKey)) {
      const cachedData = this.transferState.get(stateKey, null as unknown as T);
      this.transferState.remove(stateKey); // Cleanup
      return of(cachedData);
    }

    return this.http.post<T>(url, params).pipe(
      tap(data => this.isServer && this.transferState.set(stateKey, data)),
      catchError(error => {
        console.error(`Error fetching ${method} from ${url}:`, error);
        return of(null as unknown as T);
      })
    );
  }

  // Gallery Content Fetching
  fetchGalleryEventPageVideo(): Observable<any> {
    return this.fetchWithTransferState('galleryEventVideo', `${this.baseUrl}/display_gallery_content`, {
      account_id: this.accountId, role_id: 2, gallery_id: 1
    });
  }

  fetchGalleryEventPageImage(): Observable<any> {
    return this.fetchWithTransferState('galleryEventImage', `${this.baseUrl}/display_gallery_content`, {
      account_id: this.accountId, role_id: 2, gallery_id: 2
    });
  }

  fetchGalleryPortfolioPageImage(): Observable<any> {
    return this.fetchWithTransferState('galleryPortfolioImage', `${this.baseUrl}/display_gallery_content`, {
      account_id: this.accountId, role_id: 2, gallery_id: 3
    });
  }

  fetchGalleryPortfolioPageVideo(): Observable<any> {
    return this.fetchWithTransferState('galleryPortfolioVideo', `${this.baseUrl}/display_gallery_content`, {
      account_id: this.accountId, role_id: 2, gallery_id: 45
    });
  }

  // Article Fetching
  fetchArticleDetails(slug: string): Observable<any> {
    return this.fetchWithTransferState('articleDetails', `${this.baseUrl}/article_details`, { slug });
  }

  fetchArticlesByCategory(categoryId: string): Observable<any> {
    return this.fetchWithTransferState('articlesByCategory', `${this.baseUrl}/articles_by_category`, {
      category_id: categoryId, account_id: this.accountId
    });
  }

  fetchBlogCategories(): Observable<any> {
    return this.fetchWithTransferState('blogCategories', `${this.baseUrl}/fetch_blog_categories`, { account_id: this.accountId });
  }

  fetchAllArticleDetails(): Observable<any> {
    return this.fetchWithTransferState('allArticles', `${this.baseUrl}/latest_articles`, { account_id: this.accountId });
  }

  // Enquiry Data
  fetchAllEnquiryTypes(): Observable<any> {
    return this.fetchWithTransferState('enquiryTypes', `${this.baseUrl}/fetch_enquiry_type_by_account`, { account_id: this.accountId });
  }

  addNewEnquiry(enquiryData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/add_new_enquiry`, enquiryData).pipe(
      catchError(error => {
        console.error('Error adding new enquiry:', error);
        return of(null);
      })
    );
  }

  // About Us & Services
  fetchAboutUsData(): Observable<any> {
    return this.fetchWithTransferState('aboutUs', `${this.baseUrl}/fetch_about_us`, { account_id: this.accountId });
  }

  fetchWhyChooseUs(): Observable<any> {
    return this.fetchWithTransferState('whyChoose', `${this.baseUrl}/fetch_service`, { account_id: this.accountId });
  }
}
